// ===========================================
// README Upload Component
// Ingest README.md content to auto-populate project data
// ===========================================

import React, { useState, useRef } from 'react';
import { Upload, FileText, Loader, Check, AlertCircle, Sparkles, FolderOpen } from 'lucide-react';
import api, { projectAPI } from '../services/api';
import './ReadmeUpload.scss';

const ReadmeUpload = ({ projectId, onDataExtracted, onClose }) => {
  const [mode, setMode] = useState('upload'); // 'upload' or 'paste'
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [extractedData, setExtractedData] = useState(null);
  const [savedToVault, setSavedToVault] = useState(false); // Track if README was saved to Vault
  const fileInputRef = useRef(null);

  // ===========================================
  // Auto-save README.md to Project Vault (Files section)
  // ===========================================
  // After processing, the README is automatically saved as a file
  // so users can reference it later in the Vault tab
  const saveReadmeToVault = async (readmeContent) => {
    try {
      // Create a Blob from the README content
      const blob = new Blob([readmeContent], { type: 'text/markdown' });
      const file = new File([blob], 'README.md', { type: 'text/markdown' });
      
      // Build FormData for file upload
      const formData = new FormData();
      formData.append('file', file);
      formData.append('description', 'Project README - Auto-uploaded from Overview tab');
      
      // Upload to project files via API
      await projectAPI.uploadFile(projectId, formData);
      setSavedToVault(true);
      return true;
    } catch (err) {
      // Log but don't fail - README content is still saved to project.readmeContent
      console.warn('Failed to save README to Vault (non-critical):', err);
      return false;
    }
  };

  // Handle file upload
  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.md')) {
      setError('Please upload a markdown file (.md)');
      return;
    }

    // Validate file size (max 1MB)
    if (file.size > 1024 * 1024) {
      setError('File size must be less than 1MB');
      return;
    }

    try {
      const text = await file.text();
      setContent(text);
      setError('');
    } catch (err) {
      setError('Failed to read file');
    }
  };

  // Handle drop
  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    const file = e.dataTransfer.files[0];
    if (file && file.name.toLowerCase().endsWith('.md')) {
      const text = await file.text();
      setContent(text);
      setError('');
    } else {
      setError('Please drop a markdown file (.md)');
    }
  };

  // Process README content with AI
  const processReadme = async () => {
    if (!content.trim()) {
      setError('Please provide README content');
      return;
    }

    setLoading(true);
    setError('');

    try {
      // Send to AI endpoint for parsing - this also saves to the project
      const response = await api.post('/ai/parse-readme', {
        projectId,
        content: content.trim()
      });

      if (response.data.success) {
        setExtractedData(response.data.data);
        setSuccess(true);
        
        // Debug: Log what we got back
        console.log('README parse response:', {
          summaryGenerated: response.data.debug?.summaryGenerated,
          summaryLength: response.data.debug?.summaryLength,
          hasSummary: !!response.data.data?.readmeSummary
        });
        
        // Auto-save README.md file to project Vault
        await saveReadmeToVault(content.trim());
        
        if (onDataExtracted) {
          // Pass the full data including the generated summary
          onDataExtracted(response.data.data);
        }
      } else {
        setError(response.data.error || 'Failed to process README');
      }
    } catch (err) {
      // If AI endpoint fails, do basic parsing AND save to backend
      const basicData = parseReadmeBasic(content);
      
      // ===========================================
      // Fallback: Save extracted data directly to project
      // ===========================================
      try {
        // Build update payload with extracted data
        const updatePayload = {
          readmeContent: content.trim()
        };
        
        // Add description if extracted and not empty
        if (basicData.description) {
          updatePayload.description = basicData.description;
        }
        
        // Add tech stack if any found
        if (basicData.techStack?.length > 0) {
          updatePayload.techStack = basicData.techStack;
        }
        
        // NOTE: Don't add notes here - notes field is an array of {text, priority} objects
        // The raw README content is already saved in readmeContent field
        
        // Add services if any detected
        if (basicData.services?.length > 0) {
          updatePayload.services = basicData.services.map(s => ({
            name: s.name,
            type: s.type || 'other',
            tier: s.tier || null,
            monthlyCost: 0
          }));
        }
        
        // Add env variables if any detected
        if (basicData.envVariables?.length > 0) {
          updatePayload.envVariables = basicData.envVariables.map(e => ({
            key: e.key.toUpperCase().replace(/[^A-Z0-9_]/g, '_'),
            value: '',
            description: e.description || '',
            isSecret: e.isSecret !== false,
            environment: 'all'
          }));
        }
        
        // NOTE: Roadmap is intentionally NOT extracted from README
        // Developer should have full control over roadmap items
        
        // Save to project via update endpoint
        await api.put(`/projects/${projectId}`, updatePayload);
        
      } catch (saveErr) {
        // Log full error for debugging
        console.error('Failed to save README data:', saveErr);
        // Show actual error message from API response
        const errorMsg = saveErr.response?.data?.error?.message 
          || saveErr.response?.data?.message 
          || saveErr.message 
          || 'Unknown error';
        setError(`Failed to save: ${errorMsg}`);
        setLoading(false);
        return;
      }
      
      setExtractedData(basicData);
      setSuccess(true);
      
      // Auto-save README.md file to project Vault (fallback case)
      await saveReadmeToVault(content.trim());
      
      if (onDataExtracted) {
        onDataExtracted(basicData);
      }
    } finally {
      setLoading(false);
    }
  };

  // Basic README parsing (fallback if AI not available)
  // Enhanced to detect services and environment variables
  // NOTE: Roadmap is intentionally excluded - developer manages roadmap manually
  const parseReadmeBasic = (readme) => {
    const lines = readme.split('\n');
    const data = {
      description: '',
      techStack: [],
      notes: readme,
      services: [],      // Detected services
      envVariables: []   // Detected env variables
    };

    // Known services to detect
    const knownServices = {
      'vercel': { name: 'Vercel', type: 'hosting' },
      'netlify': { name: 'Netlify', type: 'hosting' },
      'railway': { name: 'Railway', type: 'hosting' },
      'render': { name: 'Render', type: 'hosting' },
      'heroku': { name: 'Heroku', type: 'hosting' },
      'mongodb': { name: 'MongoDB Atlas', type: 'database' },
      'postgresql': { name: 'PostgreSQL', type: 'database' },
      'postgres': { name: 'PostgreSQL', type: 'database' },
      'mysql': { name: 'MySQL', type: 'database' },
      'supabase': { name: 'Supabase', type: 'database' },
      'firebase': { name: 'Firebase', type: 'database' },
      'redis': { name: 'Redis', type: 'database' },
      'openai': { name: 'OpenAI', type: 'llm' },
      'anthropic': { name: 'Anthropic', type: 'llm' },
      'xai': { name: 'xAI', type: 'llm' },
      'grok': { name: 'xAI Grok', type: 'llm' },
      'clerk': { name: 'Clerk', type: 'auth' },
      'auth0': { name: 'Auth0', type: 'auth' },
      'stripe': { name: 'Stripe', type: 'payment' },
      'sendgrid': { name: 'SendGrid', type: 'email' },
      'resend': { name: 'Resend', type: 'email' },
      'cloudflare': { name: 'Cloudflare', type: 'cdn' },
      's3': { name: 'AWS S3', type: 'storage' }
    };

    // Detect services in content
    const contentLower = readme.toLowerCase();
    for (const [key, service] of Object.entries(knownServices)) {
      if (contentLower.includes(key)) {
        if (!data.services.some(s => s.name === service.name)) {
          data.services.push({ ...service, tier: null });
        }
      }
    }

    // Detect environment variables (common patterns)
    const envPatterns = [
      { regex: /MONGODB_URI|MONGO_URI|DATABASE_URL/gi, key: 'MONGODB_URI', desc: 'MongoDB connection string' },
      { regex: /POSTGRES_URL|POSTGRESQL_URL/gi, key: 'DATABASE_URL', desc: 'PostgreSQL connection string' },
      { regex: /OPENAI_API_KEY/gi, key: 'OPENAI_API_KEY', desc: 'OpenAI API key' },
      { regex: /XAI_API_KEY|GROK_API_KEY/gi, key: 'XAI_API_KEY', desc: 'xAI Grok API key' },
      { regex: /STRIPE_SECRET_KEY|STRIPE_API_KEY/gi, key: 'STRIPE_SECRET_KEY', desc: 'Stripe secret key' },
      { regex: /JWT_SECRET|AUTH_SECRET|NEXTAUTH_SECRET/gi, key: 'JWT_SECRET', desc: 'Authentication secret' },
      { regex: /CLERK_SECRET_KEY/gi, key: 'CLERK_SECRET_KEY', desc: 'Clerk authentication secret' },
      { regex: /SENDGRID_API_KEY/gi, key: 'SENDGRID_API_KEY', desc: 'SendGrid email API key' },
      { regex: /RESEND_API_KEY/gi, key: 'RESEND_API_KEY', desc: 'Resend email API key' }
    ];

    for (const { regex, key, desc } of envPatterns) {
      if (regex.test(readme)) {
        if (!data.envVariables.some(e => e.key === key)) {
          data.envVariables.push({ key, description: desc, isSecret: true });
        }
      }
    }

    let currentSection = '';
    let descriptionLines = [];

    for (const line of lines) {
      const trimmed = line.trim();
      
      // Skip empty lines at the start
      if (!trimmed && !currentSection && descriptionLines.length === 0) continue;

      // Detect section headers
      if (trimmed.startsWith('# ')) {
        // Main title - skip (project name)
        currentSection = 'title';
        continue;
      } else if (trimmed.startsWith('## ')) {
        const sectionName = trimmed.replace(/^##\s*/, '').toLowerCase();
        if (sectionName.includes('tech') || sectionName.includes('stack') || sectionName.includes('built')) {
          currentSection = 'tech';
        } else if (sectionName.includes('feature')) {
          currentSection = 'features';
        } else if (sectionName.includes('install') || sectionName.includes('setup') || sectionName.includes('getting')) {
          currentSection = 'setup';
        } else {
          // NOTE: Roadmap sections are intentionally skipped - developer manages roadmap manually
          currentSection = 'other';
        }
        continue;
      }

      // Extract content based on section
      if (currentSection === 'title' || currentSection === '') {
        if (trimmed && !trimmed.startsWith('#')) {
          descriptionLines.push(trimmed);
        }
      } else if (currentSection === 'tech') {
        // Extract tech from bullet points or inline
        const techMatch = trimmed.match(/^[-*]\s*\*?\*?([^*]+)\*?\*?/);
        if (techMatch) {
          const tech = techMatch[1].trim().split(/[,\-–]/)[0].trim();
          if (tech && tech.length < 30) {
            data.techStack.push(tech);
          }
        }
      }
      // NOTE: Roadmap extraction removed - developer manages roadmap manually
    }

    // Build description from first paragraph
    data.description = descriptionLines.slice(0, 3).join(' ').substring(0, 500);

    return data;
  };

  return (
    <div className="readme-upload">
      <div className="readme-upload__header">
        <div className="readme-upload__title">
          <FileText size={18} />
          <span>IMPORT README.MD</span>
        </div>
        <p className="readme-upload__subtitle">
          Upload or paste your README to auto-extract project data
        </p>
      </div>

      {/* Mode Toggle */}
      <div className="readme-upload__toggle">
        <button 
          className={`toggle-btn ${mode === 'upload' ? 'active' : ''}`}
          onClick={() => setMode('upload')}
        >
          <Upload size={14} />
          UPLOAD FILE
        </button>
        <button 
          className={`toggle-btn ${mode === 'paste' ? 'active' : ''}`}
          onClick={() => setMode('paste')}
        >
          <FileText size={14} />
          PASTE TEXT
        </button>
      </div>

      {/* Content Area */}
      <div className="readme-upload__content">
        {mode === 'upload' ? (
          <div 
            className="readme-upload__dropzone"
            onDrop={handleDrop}
            onDragOver={(e) => e.preventDefault()}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".md,.markdown"
              onChange={handleFileUpload}
              hidden
            />
            <Upload size={32} />
            <span>Drop README.md here or click to browse</span>
            <span className="readme-upload__hint">Supports .md files up to 1MB</span>
          </div>
        ) : (
          <textarea
            className="readme-upload__textarea"
            placeholder="# My Project&#10;&#10;Paste your README.md content here...&#10;&#10;## Tech Stack&#10;- React&#10;- Node.js&#10;&#10;## Roadmap&#10;- [ ] Feature 1&#10;- [x] Feature 2"
            value={content}
            onChange={(e) => setContent(e.target.value)}
            rows={12}
          />
        )}

        {/* Preview if content loaded */}
        {content && mode === 'upload' && (
          <div className="readme-upload__preview">
            <div className="preview-header">
              <Check size={14} />
              <span>FILE LOADED</span>
              <span className="preview-size">{(content.length / 1024).toFixed(1)}KB</span>
            </div>
            <pre className="preview-content">{content.substring(0, 500)}...</pre>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="readme-upload__error">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      {/* Success / Extracted Data */}
      {success && extractedData && (
        <div className="readme-upload__success">
          <div className="success-header">
            <Sparkles size={14} />
            <span>DATA EXTRACTED</span>
            {/* Vault save indicator */}
            {savedToVault && (
              <span className="vault-indicator">
                <FolderOpen size={12} />
                Saved to Vault
              </span>
            )}
          </div>
          <div className="success-data">
            {/* Description */}
            {extractedData.description && (
              <div className="data-item">
                <span className="data-label">DESCRIPTION</span>
                <span className="data-value">{extractedData.description.substring(0, 100)}...</span>
              </div>
            )}
            {/* Tech Stack */}
            {extractedData.techStack?.length > 0 && (
              <div className="data-item">
                <span className="data-label">TECH STACK</span>
                <div className="data-tags">
                  {extractedData.techStack.slice(0, 6).map((tech, i) => (
                    <span key={i} className="data-tag">{tech}</span>
                  ))}
                  {extractedData.techStack.length > 6 && (
                    <span className="data-tag data-tag--more">+{extractedData.techStack.length - 6}</span>
                  )}
                </div>
              </div>
            )}
            {/* Services Detected - NEW */}
            {extractedData.services?.length > 0 && (
              <div className="data-item">
                <span className="data-label">SERVICES DETECTED</span>
                <div className="data-tags">
                  {extractedData.services.slice(0, 6).map((service, i) => (
                    <span key={i} className="data-tag data-tag--service">
                      {service.name}
                      <small className="service-type">({service.type})</small>
                    </span>
                  ))}
                  {extractedData.services.length > 6 && (
                    <span className="data-tag data-tag--more">+{extractedData.services.length - 6}</span>
                  )}
                </div>
              </div>
            )}
            {/* Environment Variables Detected - NEW */}
            {extractedData.envVariables?.length > 0 && (
              <div className="data-item">
                <span className="data-label">ENV VARIABLES NEEDED</span>
                <div className="data-tags">
                  {extractedData.envVariables.slice(0, 6).map((env, i) => (
                    <span key={i} className="data-tag data-tag--env">{env.key}</span>
                  ))}
                  {extractedData.envVariables.length > 6 && (
                    <span className="data-tag data-tag--more">+{extractedData.envVariables.length - 6}</span>
                  )}
                </div>
              </div>
            )}
            {/* NOTE: Roadmap display removed - developer manages roadmap manually */}
          </div>
        </div>
      )}

      {/* Actions */}
      <div className="readme-upload__actions">
        {onClose && (
          <button className="btn-secondary" onClick={onClose}>
            CANCEL
          </button>
        )}
        <button 
          className="btn-primary"
          onClick={processReadme}
          disabled={loading || !content.trim()}
        >
          {loading ? (
            <>
              <Loader size={14} className="animate-spin" />
              PROCESSING...
            </>
          ) : success ? (
            <>
              <Check size={14} />
              APPLY DATA
            </>
          ) : (
            <>
              <Sparkles size={14} />
              EXTRACT DATA
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default ReadmeUpload;

